import {
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconChevronsLeft,
  IconChevronsRight,
  IconChevronUp,
  IconLoader,
} from '@tabler/icons-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ROW_ACTIVATOR_ROW_CLASS,
  RowActivator,
  type RowActivatorLink,
  shouldIgnoreRowClick,
} from '@/components/row-activator';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableRowHeader,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

/**
 * Tables already warned about a missing `getRowLabel`, keyed by column shape so
 * each distinct table complains once rather than once per render.
 */
const missingRowLabelWarnings = new Set<string>();

/**
 * A clickable row without `getRowLabel` still works by keyboard, which is why
 * this can't be an error -- but the activator's accessible name falls back to
 * the cell text, so the row announces "Acme Corp" with no hint that activating
 * it opens anything. That degrades silently and looks correct on screen, so it
 * has to be noisy in development instead.
 */
function warnMissingRowLabel(columnKeys: string): void {
  if (missingRowLabelWarnings.has(columnKeys)) return;
  missingRowLabelWarnings.add(columnKeys);
  // biome-ignore lint/suspicious/noConsole: development-only accessibility guard
  console.warn(
    '[DataTable] Rows are clickable but no `getRowLabel` was passed, so each row control ' +
      'is named by its cell text alone and never says what activating the row does. ' +
      `Pass getRowLabel={(item) => \`Open <record> \${item.name}\`}. Columns: ${columnKeys}`
  );
}

/** Tables already warned about a row header sitting on the activator cell. */
const rowHeaderCollisionWarnings = new Set<string>();

/**
 * A `<th scope="row">` is named by its contents, and the activator inside it
 * carries an `aria-label` naming an *action* -- "Open client Acme Corp". So a
 * cell that is both reports the action as the row's name, repeated on every
 * cell the user arrows through. Naming the row wrongly is worse than not
 * naming it, so the row header is dropped; that is silent, hence the warning.
 */
function warnRowHeaderOnActivator(columnKey: string): void {
  if (rowHeaderCollisionWarnings.has(columnKey)) return;
  rowHeaderCollisionWarnings.add(columnKey);
  // biome-ignore lint/suspicious/noConsole: development-only accessibility guard
  console.warn(
    `[DataTable] Column "${columnKey}" is both the row activator and the row header, so no row ` +
      "header is rendered -- the activator's aria-label would become the row's accessible name. " +
      'Point rowHeaderColumnKey or rowActivatorColumnKey at a different column.'
  );
}

// Generic interfaces
export interface ColumnConfig<T = unknown> {
  key: string;
  label: string;
  sortable?: boolean;
  align?: 'left' | 'center' | 'right';
  render: (item: T) => ReactNode;
}

export interface FilterConfig {
  key: string;
  label: string;
  options: { value: string; label: string }[];
  placeholder?: string;
  multiSelect?: boolean;
  /**
   * Suppress the auto-injected "All" item at the top of the dropdown.
   * Use when the filter is a strict toggle whose options are exhaustive
   * (e.g. file-format status: Active or Deleted, with no "show both"
   * state). Has no effect on `multiSelect` filters.
   */
  hideAllOption?: boolean;
}

export interface PaginationMeta {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface DataTableResponse<T> {
  data: T[];
  pagination?: PaginationMeta;
  meta?: PaginationMeta;
}

interface DataTableProps<T> {
  data: DataTableResponse<T> | undefined;
  columns: ColumnConfig<T>[];
  /**
   * The table's accessible name, rendered as a visually hidden `<caption>` --
   * e.g. `Clients`, `Payment files`. Required because a table announces itself
   * by nothing else: without it a screen reader user lands in an unlabelled
   * grid, and on a page showing two tables can't tell which one they're in.
   */
  caption: string;
  filters?: FilterConfig[];
  currentFilterValues?: Record<string, string | string[]>;
  headerActions?: ReactNode;
  isLoading?: boolean;
  error?: string | null;
  onRowClick?: ((item: T) => void) | undefined;
  /**
   * Per-row predicate gating both the click handler and the click
   * affordance (cursor + hover bg). When omitted, every row is clickable
   * iff `onRowClick` is provided -- the historical default. When supplied,
   * rows for which it returns `false` render with no `onClick`, no
   * pointer cursor, and no hover highlight, so the table never advertises
   * an action it won't perform.
   */
  isRowClickable?: (item: T) => boolean;
  /**
   * Accessible name for the row's activator control -- the keyboard entry
   * point for `onRowClick`. Should identify the record and the action, e.g.
   * `Open client ${item.name}`. Pass this whenever `onRowClick` is set; a row
   * whose only name is its first cell's text tells a screen reader user
   * nothing about what activating it does.
   */
  getRowLabel?: (item: T) => string;
  /**
   * Destination when the row click is pure navigation. Supplying it turns the
   * activator into a real link, so open-in-new-tab, middle-click and the
   * context menu all work. Rows that open a dialog or expand a panel omit it
   * and get a button driven by `onRowClick`.
   *
   * Enough on its own: with no `onRowClick`, clicking anywhere in the row
   * navigates here too, so the destination lives in one place. A table that
   * passes both keeps `onRowClick` in charge of the row click -- silently
   * ignoring a handler someone passed would be worse than the duplication.
   */
  getRowLink?: (item: T) => RowActivatorLink | undefined;
  /**
   * Column whose cell hosts the activator; defaults to the first column.
   * Point it at a column that identifies the record -- a date or a truncated
   * id makes a poor link.
   */
  rowActivatorColumnKey?: string;
  /**
   * Column whose cell names the row, rendered as `<th scope="row">` so a screen
   * reader repeats it while moving across the row. Usually the same column as
   * `rowActivatorColumnKey`, but it answers a different question -- "what names
   * this record?" rather than "what would you click to open it?" -- so a table
   * whose activator sits on a reference number may want its payee here.
   *
   * Opt-in, and silently inert when the key isn't in the current column set:
   * unlike the activator, a missing row header costs nothing, while guessing
   * wrong tells a screen reader that a date or a status names the record.
   *
   * Must not name the activator's column -- a cell can be one or the other,
   * never both. See `warnRowHeaderOnActivator`.
   */
  rowHeaderColumnKey?: string;
  onSortChange?: (key: string, order: 'asc' | 'desc') => void;
  onFilterChange?: (
    filterKey: string,
    value: string | string[],
    options?: { search_field?: 'payee' | 'payer' | 'ref' }
  ) => void;
  onPageChange?: (page: number) => void;
  emptyMessage?: ReactNode;
  loadingMessage?: ReactNode;
  defaultSortKey?: string;
  defaultSortOrder?: 'asc' | 'desc';
  // Controlled sort state (optional)
  currentSortKey?: string;
  currentSortOrder?: 'asc' | 'desc';
  extraFilters?: ReactNode;
  compact?: boolean;
}

export function DataTable<T>({
  data,
  columns,
  caption,
  filters = [],
  currentFilterValues,
  headerActions,
  isLoading = false,
  error = null,
  onRowClick,
  isRowClickable,
  getRowLabel,
  getRowLink,
  rowActivatorColumnKey,
  rowHeaderColumnKey,
  onSortChange,
  onFilterChange,
  onPageChange,
  emptyMessage = 'No data found',
  loadingMessage = 'Loading...',
  defaultSortKey,
  defaultSortOrder = 'desc',
  currentSortKey,
  currentSortOrder,
  extraFilters,
  compact = false,
}: DataTableProps<T>) {
  const navigate = useNavigate();
  // Seeded from the controlled prop when there is one, so a grid driven by its
  // page's filter state renders the right arrow on the first paint instead of
  // showing `defaultSortKey` for a frame and correcting it in the effect below.
  const [sortBy, setSortBy] = useState<string>(currentSortKey ?? defaultSortKey ?? '');
  const [order, setOrder] = useState<'asc' | 'desc'>(currentSortOrder ?? defaultSortOrder);
  const [sortAnnouncement, setSortAnnouncement] = useState('');
  const [filterValues, setFilterValues] = useState<Record<string, string | string[]>>(
    currentFilterValues ?? {}
  );

  // Sync internal state with controlled props when they change
  useEffect(() => {
    if (currentSortKey !== undefined) {
      setSortBy(currentSortKey);
    }
  }, [currentSortKey]);

  useEffect(() => {
    if (currentSortOrder !== undefined) {
      setOrder(currentSortOrder);
    }
  }, [currentSortOrder]);

  useEffect(() => {
    setFilterValues(currentFilterValues ?? {});
  }, [currentFilterValues]);

  // Subtle content transition when table state/content changes
  const contentRef = useRef<HTMLDivElement | null>(null);

  // Compute items & pagination first so hooks below are not conditional
  const items = data?.data || [];
  const pagination = data?.pagination || data?.meta;
  // Removed animation dependency array; we perform a single mount-time setup for transitions now

  const handleSort = (key: string, label: string) => {
    let newOrder: 'asc' | 'desc';
    if (sortBy === key) {
      // If clicking the same column, toggle the order
      newOrder = order === 'asc' ? 'desc' : 'asc';
    } else {
      // If clicking a different column, start with 'asc'
      newOrder = 'asc';
    }
    setSortBy(key);
    setOrder(newOrder);
    // Announced from the handler rather than derived from `sortBy`/`order`,
    // because the effects above write that state too whenever a parent pushes
    // new sort props -- a restored URL, a filter reset, a tab switch. Only the
    // handler can tell a sort the user performed from a state sync.
    setSortAnnouncement(`Sorted by ${label}, ${newOrder === 'asc' ? 'ascending' : 'descending'}.`);
    onSortChange?.(key, newOrder);
  };

  const getSortIcon = (key: string) => {
    if (sortBy !== key) return null;
    return order === 'asc' ? (
      <IconChevronUp className="ml-1 size-4" />
    ) : (
      <IconChevronDown className="ml-1 size-4" />
    );
  };

  /**
   * `aria-sort` belongs on the `<th>`: `columnheader` is the only role that
   * supports it, so on the sort button it would be silently dropped. A sortable
   * but inactive column reports `'none'` -- that's what marks it as sortable --
   * while a non-sortable one omits the attribute rather than advertising a
   * control it doesn't have.
   */
  const getAriaSort = (
    column: ColumnConfig<T>
  ): 'ascending' | 'descending' | 'none' | undefined => {
    if (!column.sortable) return undefined;
    if (sortBy !== column.key) return 'none';
    return order === 'asc' ? 'ascending' : 'descending';
  };

  const handleFilterChange = (filterKey: string, value: string | string[]) => {
    setFilterValues((prev) => ({ ...prev, [filterKey]: value }));
    onFilterChange?.(filterKey, value);
  };

  const handlePageChange = (newPage: number) => {
    onPageChange?.(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Resolved once, and deliberately forgiving: several tables build `columns`
  // conditionally (role-gated, `hiddenColumns`), so a key that isn't in the
  // current set must not cost the row its only keyboard entry point. Falling
  // back to the first column keeps the row reachable instead of silently
  // regressing to click-only.
  const activatorColumnIndex = (() => {
    if (rowActivatorColumnKey === undefined) return 0;
    const index = columns.findIndex((column) => column.key === rowActivatorColumnKey);
    return index === -1 ? 0 : index;
  })();

  // The mirror image of the fallback above, and deliberately so: an unresolved
  // key means no row header at all. Guessing column 0 here would tell a screen
  // reader that whatever happens to sit first -- a date, a status -- names the
  // record, which is worse than staying quiet.
  const namedRowHeaderIndex =
    rowHeaderColumnKey === undefined
      ? -1
      : columns.findIndex((column) => column.key === rowHeaderColumnKey);

  // The activator's cell can't also be the row header -- its aria-label would
  // become the row's name. Resolved once for the whole table rather than per
  // row, so a table with `isRowClickable` doesn't hand out row headers on just
  // the rows that happen not to be clickable.
  const rowHeaderCollidesWithActivator =
    Boolean(onRowClick || getRowLink) &&
    namedRowHeaderIndex !== -1 &&
    namedRowHeaderIndex === activatorColumnIndex;
  const rowHeaderColumnIndex = rowHeaderCollidesWithActivator ? -1 : namedRowHeaderIndex;

  if (import.meta.env.DEV && (onRowClick || getRowLink) && !getRowLabel) {
    warnMissingRowLabel(columns.map((column) => column.key).join(', '));
  }

  if (import.meta.env.DEV && rowHeaderCollidesWithActivator) {
    warnRowHeaderOnActivator(columns[namedRowHeaderIndex]?.key ?? '');
  }

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    el.classList.add('transition-all', 'duration-200', 'ease-out');
  }, []);

  // We avoid early returns for loading/error to preserve filter state across fetches

  return (
    <div className="flex flex-col gap-4">
      {/*
        Rendered empty from the first paint: assistive tech only announces
        changes to a region it was already watching, so one created in the same
        commit as its first message stays silent. `sr-only` is absolutely
        positioned, so an empty region costs no `gap-4` of dead space.
      */}
      <output className="sr-only" aria-live="polite" aria-atomic="true">
        {sortAnnouncement}
      </output>

      {/* Header with Filters and Actions */}
      {((filters && filters.length > 0) || extraFilters || headerActions) && (
        <div className="flex justify-between items-end gap-4 flex-wrap">
          <div className="flex items-end gap-4 flex-wrap">
            {filters.map((filter) => (
              <div key={filter.key} className="flex flex-col gap-2">
                <Label htmlFor={`${filter.key}-filter`} className="text-sm font-medium">
                  {filter.label}
                </Label>
                {filter.multiSelect ? (
                  <MultiSelect
                    id={`${filter.key}-filter`}
                    value={(filterValues[filter.key] as string[]) || []}
                    onValueChange={(value) => handleFilterChange(filter.key, value)}
                    placeholder={filter.placeholder || `Filter by ${filter.label.toLowerCase()}`}
                    options={filter.options}
                    className="w-[200px]"
                  />
                ) : (
                  <Select
                    // When `hideAllOption` is set, the dropdown has no
                    // sentinel for "no narrowing", so we don't fall back
                    // to `'__all__'` (which wouldn't match any rendered
                    // SelectItem and would break the controlled value).
                    value={
                      filter.hideAllOption
                        ? (filterValues[filter.key] as string)
                        : (filterValues[filter.key] as string) || '__all__'
                    }
                    onValueChange={(value) =>
                      handleFilterChange(filter.key, value === '__all__' ? '' : value)
                    }
                  >
                    <SelectTrigger className="w-[200px]" id={`${filter.key}-filter`}>
                      <SelectValue
                        placeholder={
                          filter.placeholder || `Filter by ${filter.label.toLowerCase()}`
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {filter.hideAllOption ? null : (
                        <SelectItem value="__all__" className="text-muted-foreground">
                          All
                        </SelectItem>
                      )}
                      {filter.options.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            ))}
            {extraFilters && <div className="flex flex-col gap-2">{extraFilters}</div>}
          </div>
          {headerActions && <div className="flex items-center gap-2">{headerActions}</div>}
        </div>
      )}

      {/* Table */}
      <div
        ref={contentRef}
        className="transition-all duration-200 ease-out opacity-100 translate-y-0 motion-reduce:transition-none motion-reduce:transform-none motion-reduce:opacity-100"
      >
        <div className="rounded-md border">
          <Table caption={caption}>
            <TableHeader>
              <TableRow>
                {columns.map((column) => (
                  <TableHead
                    key={column.key}
                    aria-sort={getAriaSort(column)}
                    className={cn(
                      column.align === 'right' && 'text-right',
                      column.align === 'center' && 'text-center',
                      compact && 'h-8'
                    )}
                  >
                    {column.sortable ? (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => handleSort(column.key, column.label)}
                        className={cn(
                          'h-auto p-0 font-semibold',
                          column.align === 'right' && 'text-right justify-end',
                          column.align === 'center' && 'text-center justify-center',
                          column.align !== 'right' &&
                            column.align !== 'center' &&
                            'text-left justify-start'
                        )}
                      >
                        {column.label}
                        {getSortIcon(column.key)}
                      </Button>
                    ) : (
                      <span className="font-semibold">{column.label}</span>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className={cn('text-center', compact ? 'py-4' : 'py-8')}
                  >
                    {typeof loadingMessage === 'string' ? (
                      <div className="inline-flex items-center text-muted-foreground">
                        <IconLoader className="mr-2 size-5 animate-spin" />
                        {loadingMessage}
                      </div>
                    ) : (
                      loadingMessage
                    )}
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className={cn('text-center text-destructive', compact ? 'py-4' : 'py-8')}
                  >
                    {error}
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className={cn('text-center text-muted-foreground', compact ? 'py-4' : 'py-8')}
                  >
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item, index) => {
                  // A row is clickable when there's a handler AND the
                  // optional predicate (default: always true) approves.
                  // Computing this once per row keeps the className, the
                  // onClick and the activator trivially in sync -- you can't
                  // ship a pointer cursor without the matching handler, or
                  // vice versa.
                  const clickable = Boolean(
                    (onRowClick || getRowLink) &&
                      (isRowClickable === undefined || isRowClickable(item))
                  );
                  const rowLink = clickable ? getRowLink?.(item) : undefined;
                  return (
                    <TableRow
                      key={
                        typeof item === 'object' && item !== null && 'id' in item
                          ? String((item as unknown as { id: string | number }).id)
                          : index
                      }
                      className={cn(
                        clickable && 'cursor-pointer',
                        clickable && ROW_ACTIVATOR_ROW_CLASS
                      )}
                      onClick={
                        clickable
                          ? (event) => {
                              if (shouldIgnoreRowClick(event)) return;
                              // `onRowClick` stays in charge when both are
                              // given; otherwise the link is the row's single
                              // source for where it goes.
                              if (onRowClick) {
                                onRowClick(item);
                              } else if (rowLink) {
                                navigate(
                                  rowLink.to,
                                  rowLink.state !== undefined ? { state: rowLink.state } : undefined
                                );
                              }
                            }
                          : undefined
                      }
                    >
                      {columns.map((column, columnIndex) => {
                        const hostsActivator = clickable && columnIndex === activatorColumnIndex;
                        const content = column.render(item);
                        // A row header is the same cell in a different element,
                        // so only the component varies -- contents and classes
                        // stay put.
                        const Cell =
                          columnIndex === rowHeaderColumnIndex ? TableRowHeader : TableCell;
                        return (
                          <Cell
                            key={column.key}
                            className={cn(
                              column.align === 'right' && 'text-right',
                              column.align === 'center' && 'text-center',
                              compact && 'py-1 px-2'
                            )}
                          >
                            {hostsActivator ? (
                              <RowActivator
                                link={rowLink}
                                label={getRowLabel?.(item)}
                                onActivate={() => onRowClick?.(item)}
                                // A <button> shrink-wraps its content even when
                                // displayed as a block, so the box has to be
                                // placed as well as the text aligned.
                                className={cn(
                                  column.align === 'right' && 'ml-auto',
                                  column.align === 'center' && 'mx-auto'
                                )}
                              >
                                {content}
                              </RowActivator>
                            ) : (
                              content
                            )}
                          </Cell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Row count + Pagination */}
      {pagination && pagination.total > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {(pagination.page - 1) * pagination.pageSize + 1} to{' '}
            {Math.min(pagination.page * pagination.pageSize, pagination.total)} of{' '}
            {pagination.total} results
          </div>

          {pagination.totalPages > 1 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(1)}
                disabled={pagination.page <= 1}
              >
                <IconChevronsLeft className="size-4" aria-hidden />
                <span className="sr-only">First page</span>
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
              >
                <IconChevronLeft className="size-4" aria-hidden />
                <span className="sr-only">Previous page</span>
              </Button>

              <span className="text-sm font-medium px-2">
                Page {pagination.page} of {pagination.totalPages}
              </span>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
              >
                <IconChevronRight className="size-4" aria-hidden />
                <span className="sr-only">Next page</span>
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={pagination.page >= pagination.totalPages}
              >
                <IconChevronsRight className="size-4" aria-hidden />
                <span className="sr-only">Last page</span>
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
